/**
 * "Dringend benötigte Betreuungen" — Übernahme aus dem CRM-1.0-Dashboard
 * (sh24-admin screens/Dashboard.js, components/Table2.js, AccountStatus.js,
 * CareLevel.js, App.css .table-row.priority1/2).
 *
 * Datenquelle: GET /v1/admin/urgent-cares (aktive PBs mit Startdatum in den
 * nächsten 8 Wochen). Das Widget lädt selbst, weil Sortierung, Blättern und
 * "pro Seite" serverseitig laufen (orderBy[field] + order 1/-1, Default
 * startdatum aufsteigend).
 *
 * Darstellung wie CRM 1.0:
 *  - Konto: 20-px-Punkt in der Kontostatus-Farbe
 *  - Name als Link → PB-Detail
 *  - Startdatum TT.MM.JJJJ, "Heute" wenn heute, "-" wenn ungültig
 *  - Pflegegrad: 20-px-Kreis mit Rand, angezeigt = Rohwert − 4
 *  - Zeilenfarbe nach priority: 1 = rot (noch nie eine bestätigte
 *    Betreuung), 2 = grün (Anschlussbetreuung geplant)
 *  - "Bearbeiten"-Button mit Stift → PB-Detail
 */
import React, { useEffect, useState } from 'react';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { Icon } from '../../../../components/icons';
import { navigateGlobal, buildPath } from '../../../../lib/route';
import { AppLink, rowLinkProps } from '../../../../lib/openLink';
import { v1Dashboard, type LaravelPagination, type V1User } from '../../../../lib/v1';
import {
  WidgetPager, isPerPage, nextSort, readWidgetPref, writeWidgetPref,
} from './widgetTable';

/** CRM 1.0 AccountStatus — Index = users.konto. */
export const ACCOUNT_STATUS_COLORS = ['#E5614F', '#f1a930', '#2798D8', '#b845ec', '#2FC08C', '#7B919D'];

/** CRM 1.0 CareLevel — Index = angezeigter Pflegegrad (Rohwert − 4). */
export const CARE_LEVEL_COLORS = ['#24ca40', '#ffb204', '#ff6f2b', '#fb381d', '#aa198d'];

/** Zeilen-Hintergründe je priority (hell = CRM 1.0, dunkel = abgeleitet). */
export function priorityRowColors(priority: number | null | undefined, dark: boolean): { bg: string; hover: string } {
  if (priority === 1) return dark ? { bg: '#3a1d21', hover: '#452328' } : { bg: '#ffe3e3', hover: '#ffdfdf' };
  if (priority === 2) return dark ? { bg: '#12301f', hover: '#173a26' } : { bg: '#e2f7e8', hover: '#dbf2e2' };
  return dark ? { bg: 'transparent', hover: '#1a2230' } : { bg: 'transparent', hover: '#F6F8FA' };
}

/**
 * Startdatum wie CRM 1.0 (moment): "Heute" für heute, sonst TT.MM.JJJJ,
 * "-" bei ungültigem/leerem Wert. Reine Datumsangaben (YYYY-MM-DD) werden als
 * lokales Datum gelesen, damit keine Zeitzonen-Verschiebung entsteht.
 */
export function formatStartDate(value: unknown, todayLabel: string, now: Date = new Date()): string {
  if (value == null || value === '') return '-';
  const s = String(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  let d: Date;
  if (m && s.length <= 10) d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  else d = new Date(s.replace(' ', 'T'));
  if (isNaN(d.getTime())) return '-';
  if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()) {
    return todayLabel;
  }
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mm}.${d.getFullYear()}`;
}

/** Angezeigter Pflegegrad (Rohwert − 4) oder null, wenn kein Wert (CRM 1.0: value <= 0 → nichts). */
export function displayCareLevel(raw: unknown): number | null {
  if (raw == null || raw === 'null') return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n - 4;
}

function userName(u: V1User, fallback: string): string {
  const full = (u.fullname ?? '').trim();
  if (full) return full;
  const parts = [u.vorname, u.nachname].filter(Boolean).join(' ').trim();
  return parts || fallback;
}

type SortState = { field: string | null; order: 0 | 1 | -1 };

interface UrgentCaresWidgetProps {
  mode: 'full' | 'summary';
  dark: boolean;
  tr: (k: string, opts?: Record<string, unknown>) => string;
  /** Meldet die Gesamtzahl (pagination.total) für den Kartentitel. */
  onTotal?: (total: number | null) => void;
}

const SUMMARY_ROWS = 5;

export function UrgentCaresWidget({ mode, dark, tr, onTotal }: UrgentCaresWidgetProps) {
  const t = getThemeColors(dark);
  const [perPage, setPerPage] = useState<number>(() => readWidgetPref('urgentCares.perPage', 25, isPerPage));
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ field: null, order: 0 });
  const [result, setResult] = useState<LaravelPagination<V1User> | null>(null);
  // Ladezustand abgeleitet statt per setState im Effect: lädt, solange die
  // zuletzt beantwortete Anfrage nicht der aktuellen entspricht.
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);

  const effPerPage = mode === 'summary' ? SUMMARY_ROWS : perPage;

  const requestKey = JSON.stringify([page, effPerPage, sort.field, sort.order]);
  const loading = loadedKey !== requestKey;

  useEffect(() => {
    let alive = true;
    v1Dashboard.urgentCares({
      page,
      perPage: effPerPage,
      ...(sort.field ? { orderBy: { field: sort.field }, order: sort.order } : {}),
    })
      .then(p => {
        if (!alive) return;
        setResult(p);
        setError(false);
        onTotal?.(p.total);
        // Wie CRM 1.0: steht die Seite hinter der letzten, auf die letzte springen.
        if (p.last_page > 0 && p.current_page > p.last_page) setPage(p.last_page);
      })
      .catch(() => { if (alive) { setError(true); onTotal?.(null); } })
      .finally(() => { if (alive) setLoadedKey(requestKey); });
    return () => { alive = false; };
  }, [page, effPerPage, sort.field, sort.order, requestKey, onTotal]);

  const changeSort = (field: string) => {
    setSort(prev => nextSort(prev, field));
    setPage(1);
  };

  const changePerPage = (n: number) => {
    setPerPage(n);
    writeWidgetPref('urgentCares.perPage', n);
    setPage(1);
  };

  const openPb = (id: number) => navigateGlobal('pflege', String(id));

  const rows = result?.data ?? [];

  if (!result) {
    return (
      <div style={{ fontSize: 12, color: t.muted }}>
        {error ? tr('dashboard.widget.load_error') : tr('dashboard.widget.loading')}
      </div>
    );
  }

  const thStyle: React.CSSProperties = {
    textAlign: 'left', fontSize: 11, fontWeight: 600, color: t.muted,
    padding: '6px 8px', borderBottom: `1px solid ${t.border}`, whiteSpace: 'nowrap',
    userSelect: 'none',
  };
  const tdStyle: React.CSSProperties = {
    padding: '7px 8px', fontSize: 12, color: t.text, borderBottom: `1px solid ${t.borderSoft}`,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle',
  };

  const sortTh = (field: string, label: string, width?: number | string) => {
    const active = sort.field === field;
    const ariaSort = active ? (sort.order === 1 ? 'ascending' : 'descending') : 'none';
    return (
      <th key={field} style={{ ...thStyle, width }} aria-sort={ariaSort}>
        <button
          type="button"
          onClick={() => changeSort(field)}
          title={tr('dashboard.widget.table.sort_by', { column: label || tr('dashboard.widget.urgent_cares.col_konto') })}
          style={{
            background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
            font: 'inherit', color: active ? BRAND : t.muted, display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          {label}
          <span aria-hidden="true" style={{ fontSize: 9, opacity: active ? 1 : 0.5 }}>
            {active ? (sort.order === 1 ? '▲' : '▼') : '↕'}
          </span>
        </button>
      </th>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <div style={{ overflowX: 'auto', opacity: loading ? 0.6 : 1, transition: 'opacity 0.15s' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 420 }}>
          <thead>
            <tr>
              {sortTh('konto', '', 44)}
              {sortTh('id', tr('dashboard.widget.urgent_cares.col_name'))}
              {sortTh('startdatum', tr('dashboard.widget.urgent_cares.col_start'), 110)}
              {sortTh('pflegegrad', tr('dashboard.widget.urgent_cares.col_care_level'), 96)}
              <th style={{ ...thStyle, width: 116 }}><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>{tr('dashboard.widget.urgent_cares.col_actions')}</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(u => {
              const colors = priorityRowColors(u.priority, dark);
              const name = userName(u, tr('dashboard.widget.urgent_cares.unknown_name', { id: u.id }));
              const pg = displayCareLevel(u.pflegegrad);
              const konto = typeof u.konto === 'number' ? u.konto : Number(u.konto);
              const prioRow = u.priority === 1 || u.priority === 2;
              return (
                <tr
                  key={u.id}
                  data-priority={u.priority ?? 0}
                  onMouseEnter={() => setHovered(u.id)}
                  onMouseLeave={() => setHovered(h => (h === u.id ? null : h))}
                  style={{ background: hovered === u.id ? colors.hover : colors.bg }}
                >
                  <td style={tdStyle}>
                    <span
                      role="img"
                      aria-label={tr(`dashboard.widget.urgent_cares.account_status.${konto}`)}
                      title={tr(`dashboard.widget.urgent_cares.account_status.${konto}`)}
                      style={{
                        display: 'inline-block', width: 20, height: 20, borderRadius: '50%',
                        background: ACCOUNT_STATUS_COLORS[konto] ?? 'transparent', verticalAlign: 'middle',
                      }}
                    />
                  </td>
                  <td style={tdStyle}>
                    <AppLink
                      page="pflege"
                      sub={String(u.id)}
                      style={{
                        display: 'block', textDecoration: 'none', cursor: 'pointer',
                        fontWeight: 600, color: BRAND, maxWidth: '100%',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left',
                      }}
                    >
                      {name}
                    </AppLink>
                  </td>
                  <td style={tdStyle}>{formatStartDate(u.startdatum, tr('dashboard.widget.urgent_cares.today'))}</td>
                  <td style={tdStyle}>
                    {pg != null && (
                      <span
                        data-testid="care-level"
                        title={tr('dashboard.widget.urgent_cares.care_level_title', { level: pg })}
                        style={{
                          display: 'inline-block', boxSizing: 'border-box', width: 20, height: 20,
                          border: '1px solid', borderRadius: '50%', fontSize: 13, fontWeight: 500,
                          lineHeight: '18px', textAlign: 'center',
                          color: CARE_LEVEL_COLORS[pg] ?? t.text,
                        }}
                      >
                        {pg}
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      type="button"
                      {...rowLinkProps(buildPath('pflege', String(u.id)), () => openPb(u.id))}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 9px', borderRadius: 7, fontSize: 11.5, fontWeight: 500,
                        border: `1px solid ${prioRow ? (dark ? '#5b6878' : '#9da8b1') : t.border}`,
                        background: dark ? t.card : '#fff', color: t.text, cursor: 'pointer',
                        fontFamily: 'Outfit, sans-serif',
                      }}
                    >
                      <Icon.Edit c={t.text} /> {tr('dashboard.widget.urgent_cares.edit')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && (
          <div style={{ padding: '14px 8px', fontSize: 12, color: t.muted }}>
            {tr('dashboard.widget.table.no_results')}
          </div>
        )}
      </div>
      {mode === 'full' && (
        <WidgetPager
          dark={dark}
          page={result.current_page}
          lastPage={result.last_page}
          total={result.total}
          perPage={perPage}
          onPage={setPage}
          onPerPage={changePerPage}
          tr={tr}
        />
      )}
    </div>
  );
}
